/**
 * Deferred promise
 *
 * Use deferred promise if the promise cannot be resolved in Promise callback.
 * E.g. in event based messages
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Mozilla/JavaScript_code_modules/Promise.jsm/Deferred#backwards_forwards_compatible}
 */
export class Deferred<T> {
    promise: Promise<T>;

    resolve!: (value: T | PromiseLike<T>) => void;
    reject!: (reason: Error) => void;

    then: <TResultFulfill = T, TResultReject = never>(
        onfulfilled?:
            | ((value: T) => TResultFulfill | PromiseLike<TResultFulfill>)
            | undefined
            | null,
        onrejected?:
            | ((reason: Error) => TResultReject | PromiseLike<TResultReject>)
            | undefined
            | null
    ) => Promise<TResultFulfill | TResultReject>;

    catch: <TResultReject = never>(
        onrejected?:
            | ((reason: Error) => TResultReject | PromiseLike<TResultReject>)
            | undefined
            | null
    ) => Promise<T | TResultReject>;

    /**
     * Creates an instance of Deferred.
     * Initialize promise object and chaining functions
     */
    constructor() {
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });

        this.then = this.promise.then.bind(this.promise);
        this.catch = this.promise.catch.bind(this.promise);
        Object.freeze(this);
    }

    /**
     * Create a resolved Deferred object
     */
    static resolved(): Deferred<void>;
    static resolved<TResult>(data: TResult | PromiseLike<TResult>): Deferred<TResult>;
    static resolved<TResult>(
        data?: TResult | PromiseLike<TResult>
    ): Deferred<void> | Deferred<TResult> {
        if (data) {
            const deferred: Deferred<TResult> = new Deferred();
            deferred.resolve(data);
            return deferred;
        } else {
            const deferred: Deferred<void> = new Deferred();
            deferred.resolve();
            return deferred;
        }
    }

    /**
     * Create a rejected Deferred object
     */
    static rejected(error: Error): Deferred<never> {
        const deferred: Deferred<never> = new Deferred();
        deferred.reject(error);
        return deferred;
    }
}
